import { Box, Typography } from "@material-ui/core";
import { TFunction, useTranslation } from "react-i18next";
import FlexTypographyComponent from "../../../../components/FlexTypographyComponent";
import FlexTypographyArray from "../../../../components/LyfeTable/FlexTypographyArray";
import LyfeAvatar from "../../../../components/LyfeAvatar";
import LyfegenLogo from "../../../../components/icons/LyfegenLogo";
import MappedComponent from "../../../../components/mapped-components/MappedComponent";
import { tableIdMinWidth } from "../../../../hooks/use-map-theme-styles";
import { EMPTY_VALUE_PLACEHOLDER } from "../../../../models/common/data";
import { OccurrenceEnum } from "../../../../models/generated/graphql";
import { Column } from "../../../../types/tableTypes";
import { DeletedUserEmail } from "../../../../utils/enum-helpers";
import { localeDate } from "../../../../utils/locale-date";
import { splitName } from "../../../../utils/name-to-initials";
import { GetFormFlexEntitiesQuery } from "../../types/queries.generated";

type FormColumn = Column<GetFormFlexEntitiesQuery["flexEntities"][number]>;

export function getFormColumns(t: TFunction<string>): Array<FormColumn> {
	return [
		{
			title: t("form-id"),
			field: "id",
			default: true,
			render: (rawData: any) => <FlexTypographyComponent text={rawData.id} />,
		},
		{
			title: t("externalFormId"),
			field: "externalFormId",
			default: false,
			render: (rawData: any) => <FlexTypographyComponent text={rawData.externalFormId || EMPTY_VALUE_PLACEHOLDER} />,
		},
		{
			title: t("type"),
			field: "flexEntityName",
			default: true,
			render: (rowData) => <FlexTypographyComponent text={t(rowData.flexEntityName!)} />,
		},
		{
			title: t("status"),
			field: "status",
			default: true,
			render: (rowData) => <MappedComponent component="FormsStatusComponent" props={rowData} />,
		},
		{
			title: t("patientId"),
			field: "patientId",
			default: true,
			render: (rowData) => <FlexTypographyComponent text={rowData?.patientId ?? EMPTY_VALUE_PLACEHOLDER} />,
		},
		{
			title: t("product"),
			field: "preparationBrandNames",
			default: true,
			render: (rowData) => <FlexTypographyArray rowData={rowData} fieldAssessor="preparationBrandNames" />,
		},
		{
			title: t("decisionTherapy"),
			field: "decisionTherapy",
			default: true,
			render: (rowData) => (
				<FlexTypographyComponent
					text={rowData.decisionTherapy ? t(`decisions-${rowData.decisionTherapy}`) : EMPTY_VALUE_PLACEHOLDER}
				/>
			),
		},
		{
			title: t("occurrence"),
			field: "occurrence",
			default: true,
			render: (rowData) => <FlexTypographyComponent text={t(rowData.occurrence!)} />,
		},
		{
			title: t("added-on"),
			field: "createdOn",
			default: true,
			align: "right",
			render: (rowData) => (
				<FlexTypographyComponent containerStyles={{ justifyContent: "flex-end" }} text={localeDate(rowData?.createdOn)!} />
			),
		},
		{
			title: t("added-by"),
			field: "viewCreatedByName",
			default: true,
			render: (rowData) => {
				const isDeletedUser = rowData.createdByEmail === DeletedUserEmail;

				return (
					<Box width="100%">
						<LyfeAvatar
							toolTipTitle={isDeletedUser ? t("deleted-user") : ""}
							isSystemAvatar={rowData.occurrence === OccurrenceEnum.AutoGenerated}
							userEmail={rowData.createdByEmail!}
							firstName={
								rowData?.occurrence === OccurrenceEnum.AutoGenerated
									? t("system-generated")
									: splitName(rowData.viewCreatedByName)[0] || ""
							}
							lastName={
								rowData?.occurrence === OccurrenceEnum.AutoGenerated ? "" : splitName(rowData.viewCreatedByName)[1] || ""
							}
							size="small"
							initialsProps={{ style: { fontSize: "14px" } }}
						>
							{rowData.occurrence === OccurrenceEnum.AutoGenerated ? <LyfegenLogo /> : undefined}
						</LyfeAvatar>
					</Box>
				);
			},
		},
	];
}

export default function useFormColumns(): { columns: Array<FormColumn> } {
	const { t } = useTranslation();
	const columns = getFormColumns(t);

	return { columns };
}
